import { Toast } from '../../../components/Common/Toast';
import { callAPI } from '../../../services/api.service';
import { HTTPStatusEnum } from '../../../interfaces/log.interface';
import { TokenCookieManager } from '../../../utils/tokenCookies';

export class GoogleClassroomAuthTabService {
  private static popupWindow: Window | null = null;
  private static authPromise: Promise<boolean> | null = null;

  /**
   * Open Google Classroom OAuth popup and handle authentication
   * @returns Promise that resolves to true if authentication was successful
   */
  static async authenticateWithTab(): Promise<boolean> {
    if (this.authPromise) {
      return this.authPromise;
    }

    this.authPromise = new Promise((resolve) => {
      try {
        // Generate OAuth URL
        const authUrl = this.generateAuthUrl();

        // Open popup window
        this.popupWindow = window.open(
          authUrl,
          'googleClassroomAuth',
          'width=500,height=600,scrollbars=yes,resizable=yes',
        );

        if (!this.popupWindow) {
          Toast('Popup blocked. Please allow popups for this site.', true);
          resolve(false);
          return;
        }

        // Poll for popup closure or message
        const pollInterval = setInterval(() => {
          if (this.popupWindow?.closed) {
            clearInterval(pollInterval);
            this.cleanup();
            resolve(false);
          }
        }, 1000);

        // Listen for message from popup
        const messageHandler = async (event: MessageEvent) => {
          if (event.origin !== window.location.origin) {
            return;
          }

          if (event.data.type === 'GOOGLE_CLASSROOM_AUTH_SUCCESS') {
            clearInterval(pollInterval);
            this.cleanup();
            resolve(true);
          } else if (event.data.type === 'GOOGLE_CLASSROOM_AUTH_ERROR') {
            clearInterval(pollInterval);
            this.cleanup();
            Toast(event.data.error || 'Authentication failed', true);
            resolve(false);
          }
        };

        window.addEventListener('message', messageHandler);

        // Cleanup function
        const cleanup = () => {
          clearInterval(pollInterval);
          window.removeEventListener('message', messageHandler);
          this.cleanup();
        };

        // Set timeout for authentication
        setTimeout(() => {
          if (this.popupWindow && !this.popupWindow.closed) {
            this.popupWindow.close();
            cleanup();
            resolve(false);
          }
        }, 300000); // 5 minutes timeout
      } catch (error) {
        console.error('Error in Google Classroom authentication:', error);
        this.cleanup();
        resolve(false);
      }
    });

    return this.authPromise;
  }

  /**
   * Get existing token or authenticate if needed
   * @returns Promise that resolves to true if token is available
   */
  static async getTokenOrAuthenticate(): Promise<boolean> {
    try {
      // Check if we already have integration
      const hasIntegration = await this.checkIntegrationStatus();
      if (hasIntegration) {
        return true;
      }

      // If no integration, authenticate
      return await this.authenticateWithTab();
    } catch (error) {
      console.error('Error getting token or authenticating:', error);
      return false;
    }
  }

  /**
   * Check current integration status
   * @returns Promise that resolves to true if integration exists
   */
  static async checkIntegrationStatus(): Promise<boolean> {
    try {
      const response = await callAPI('api/google-classroom/courses', null, 'GET');
      return response?.status === HTTPStatusEnum.OK;
    } catch (error) {
      return false;
    }
  }

  /**
   * Revoke Google Classroom integration
   * @returns Promise that resolves to true if revocation was successful
   */
  static async revokeIntegration(): Promise<boolean> {
    try {
      const response = await callAPI('api/google-classroom/integration', null, 'DELETE');
      return response?.status === HTTPStatusEnum.OK;
    } catch (error) {
      console.error('Error revoking Google Classroom integration:', error);
      return false;
    }
  }

  /**
   * Generate OAuth authorization URL
   * @returns OAuth URL string
   */
  private static generateAuthUrl(): string {
    const token = TokenCookieManager.getToken() || '';
    const params = new URLSearchParams({
      authType: 'teacher-sign-up',
      isPopup: 'true',
      token: token,
    });

    return `${process.env.REACT_APP_API_URL}/api/auth/google-classroom/authorize?${params.toString()}`;
  }

  /**
   * Cleanup popup window and reset state
   */
  private static cleanup(): void {
    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.close();
    }
    this.popupWindow = null;
    this.authPromise = null;
  }

  /**
   * Close popup window if open
   */
  static closePopup(): void {
    this.cleanup();
  }
}
